// frontend/src/api/users.ts - COMPLETE WITH LEVEL SYSTEM METHODS
import { api } from './client';
import { ensureStringId } from './helpers';
import type { ApiResponse, User, DashboardStats, PaginationData, LeaderboardEntry } from '../types';

export const usersAPI = {
  health: () => api.get<ApiResponse>('/api/users/health'),
  
  getAll: (params?: { 
    page?: number; 
    limit?: number; 
    role?: string; 
    search?: string;
    level?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => api.get<ApiResponse<{ users: User[]; pagination: PaginationData; total: number }>>('/api/users', { params }),
  
  getById: async (id: string) => {
    const idString = ensureStringId(id);
    console.log('📡 usersAPI.getById called with:', id, '→', idString);
    
    if (!idString || idString === '[object Object]') {
      return Promise.reject(new Error('Invalid ID format'));
    }
    
    try {
      const response = await api.get<ApiResponse<{ user: User; stats: DashboardStats; about?: any }>>(`/api/users/${idString}`);
      return response;
    } catch (error: any) {
      console.error('❌ usersAPI.getById error:', error.response?.data || error.message);
      throw error;
    }
  },
  
  getStats: (id?: string) => {
    const endpoint = id ? `/api/users/${ensureStringId(id)}/stats` : '/api/users/me/stats';
    return api.get<ApiResponse<DashboardStats>>(endpoint);
  },
  
  update: (id: string, data: Partial<User>) => {
    const idString = ensureStringId(id);
    return api.put<ApiResponse<{ user: User }>>(`/api/users/${idString}`, data);
  },
  
  search: (query: string, params?: { role?: string; level?: string; limit?: number }) =>
    api.get<ApiResponse<{ users: User[] }>>('/api/users/search', { params: { query, ...params } }),
  
  getActiveCount: () => api.get<ApiResponse<{ count: number }>>('/api/users/active/count'),
  
  getOnlineStatus: (userId: string) => {
    const idString = ensureStringId(userId);
    return api.get<ApiResponse<{ isOnline: boolean; lastSeen?: string }>>(`/api/users/${idString}/online`);
  },
  
  updateOnlineStatus: (isOnline: boolean) =>
    api.put<ApiResponse>(`/api/users/online`, { isOnline }),
  
  blockUser: (userId: string, data: { reason: string }) => {
    const idString = ensureStringId(userId);
    return api.post<ApiResponse>(`/api/users/${idString}/block`, data);
  },
  
  unblockUser: (userId: string) => {
    const idString = ensureStringId(userId);
    return api.post<ApiResponse>(`/api/users/${idString}/unblock`);
  },
  
  getBlockedUsers: () => api.get<ApiResponse<{ users: User[] }>>('/api/users/blocked'),
  
  // Level System Methods (NEW)
  getLeaderboard: (role: 'rider' | 'customer', limit?: number) =>
    api.get<ApiResponse<{ role: string; leaderboard: LeaderboardEntry[]; total: number }>>(
      `/api/users/leaderboard/${role}`,
      { params: { limit } }
    ),
  
  recalculateLevel: (userId?: string) => {
    const id = userId ? ensureStringId(userId) : 'me';
    return api.post<ApiResponse<{ userLevel: string; levelInfo: any; levelUpdatedAt: string }>>(
      `/api/users/${id}/recalculate-level`
    );
  },
  
  getUserLevelInfo: (userId: string) => {
    const idString = ensureStringId(userId);
    return api.get<ApiResponse<{ user: User; stats: any }>>(`/api/users/${idString}`);
  },
};

export default usersAPI;